import { Pagination } from '@cloudscape-design/components';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  getDocs,
  limit,
  query,
  startAfter,
} from 'firebase/firestore';
import React, { useCallback, useEffect, useState } from 'react';

export default function useCustomInfiniteQuery({
  queryKey,
  queryParams,
  pageSize,
  staleTime,
  transformData,
}) {
  const [page, setPage] = useState(1);

  const fetchPaginatedData = useCallback(async ({
    pageParam = null,
    queryParamsArr,
    pageSizeNum,
  }) => {
    let q;
    if (pageSizeNum > 0) {
      q = query(
        ...queryParamsArr,
        limit(pageSizeNum),
      );
    } else {
      q = query(
        ...queryParamsArr,
      );
    }

    if (pageParam && pageSizeNum > 0) {
      q = query(
        ...queryParamsArr,
        startAfter(pageParam),
        limit(pageSizeNum),
      );
    }

    try {
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map((doc) => {
        const docData = { id: doc.id, ...doc.data() };
        if (typeof transformData === 'function') {
          try {
            return transformData(doc);
          } catch (error) {
          // eslint-disable-next-line no-console
            console.error('Error transforming data for doc:', doc.id, error);
            return docData;
          }
        }
        return docData;
      });

      return {
        data,
        lastVisible: snapshot.docs.length < pageSizeNum
          ? null
          : snapshot.docs[snapshot.docs.length - 1],
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching paginated data:', error);
      return {
        data: [],
        lastVisible: null,
      };
    }
  }, [transformData]);

  const {
    data,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [queryKey, queryParams],
    queryFn: ({ pageParam }) => fetchPaginatedData({
      pageParam,
      queryParamsArr: queryParams,
      pageSizeNum: pageSize,
    }),
    getNextPageParam: (lastPage) => lastPage.lastVisible || null,
    initialPageParam: null,
    enabled: queryParams.length > 0, // Only run if queryParams are provided
    ...(staleTime !== undefined && { staleTime }),
  });

  // Reset page to 1 when queryParams change, like filters or sorting
  useEffect(() => {
    setPage(1);
  }, [queryParams]);

  const handlePageChange = async (newPage) => {
    if (newPage > data?.pages.length) {
      await fetchNextPage();
    }
    setPage(newPage);
  };

  function CustomPagination() {
    if (!pageSize) {
      return null;
    }
    return (
      <Pagination
        currentPageIndex={page}
        pagesCount={data?.pages?.length || page}
        openEnd={hasNextPage}
        onChange={({ detail }) => handlePageChange(detail.currentPageIndex)}
        disabled={isFetchingNextPage}
      />
    );
  }

  return {
    data,
    error,
    refetch,
    isFetchingNextPage,
    pageData: data?.pages?.[page - 1]?.data || [],
    CustomPagination,
  };
}
